/**
 * Settings → Approval Workflows (Section 4C).
 *
 * HR admins design approval chains per entity type (leave, expense, etc.)
 * Each workflow is an ordered list of steps. A step is: an approver
 * (direct manager / HR / role / specific user / …), an optional gate
 * condition (e.g. ``total_days >= 2``), and an ``auto_approve`` flag
 * that short-circuits the step when the condition is NOT met.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowDown, Pencil, Plus, Trash2, Wand2, X } from "lucide-react";

import { useToast } from "@/hooks/useToast";

import {
  APPROVER_TYPE_LABELS,
  ENTITY_TYPE_LABELS,
  createWorkflow,
  deleteWorkflow,
  listWorkflows,
  seedDefaultWorkflows,
  updateWorkflow,
  type ApproverType,
  type ConditionOperator,
  type EntityType,
  type StepCondition,
  type Workflow,
  type WorkflowStep,
} from "@/api/approvalWorkflows";

import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";

const ENTITY_OPTIONS: { value: EntityType; label: string }[] = (
  Object.keys(ENTITY_TYPE_LABELS) as EntityType[]
).map((k) => ({ value: k, label: ENTITY_TYPE_LABELS[k] }));

const APPROVER_OPTIONS: { value: ApproverType; label: string }[] = (
  Object.keys(APPROVER_TYPE_LABELS) as ApproverType[]
).map((k) => ({ value: k, label: APPROVER_TYPE_LABELS[k] }));

const OPERATOR_OPTIONS: { value: ConditionOperator; label: string }[] = [
  { value: "==", label: "= equals" },
  { value: "!=", label: "≠ not equal" },
  { value: ">", label: "> greater than" },
  { value: ">=", label: "≥ at least" },
  { value: "<", label: "< less than" },
  { value: "<=", label: "≤ at most" },
  { value: "in", label: "in list" },
  { value: "not_in", label: "not in list" },
];

const ROLE_SUGGESTIONS = [
  { value: "HR_ADMIN", label: "HR Admin" },
  { value: "MANAGER", label: "Manager" },
  { value: "SUPER_ADMIN", label: "Super Admin" },
  { value: "EMPLOYEE", label: "Employee" },
];

function emptyStep(order: number): WorkflowStep {
  return {
    step_order: order,
    approver_type: "direct_manager",
    approver_id: null,
    approver_role: null,
    condition: null,
    auto_approve: false,
  };
}

function summarizeCondition(cond: StepCondition | null | undefined): string {
  if (!cond) return "always";
  const v = Array.isArray(cond.value) ? cond.value.join(", ") : String(cond.value);
  return `${cond.field} ${cond.operator} ${v}`;
}

function summarizeApprover(step: WorkflowStep): string {
  if (step.approver_type === "role" && step.approver_role) {
    return `Role: ${step.approver_role}`;
  }
  if (step.approver_type === "specific_user" && step.approver_id) {
    return `User ${step.approver_id.slice(0, 8)}…`;
  }
  return APPROVER_TYPE_LABELS[step.approver_type];
}

export function WorkflowsSettings() {
  const { addToast } = useToast();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Workflow | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listWorkflows();
      setWorkflows(rows);
    } catch (e: any) {
      addToast(
        "error",
        `Couldn't load workflows: ${e?.response?.data?.detail ?? "Try again."}`,
      );
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const grouped = useMemo(() => {
    const map = new Map<EntityType, Workflow[]>();
    for (const wf of workflows) {
      const list = map.get(wf.entity_type) ?? [];
      list.push(wf);
      map.set(wf.entity_type, list);
    }
    return map;
  }, [workflows]);

  const handleDelete = async (wf: Workflow) => {
    if (!confirm(`Delete workflow "${wf.name}"?`)) return;
    try {
      await deleteWorkflow(wf.id);
      addToast("success", "Workflow deleted");
      await refresh();
    } catch (e: any) {
      addToast(
        "error",
        `Couldn't delete: ${e?.response?.data?.detail ?? "Try again."}`,
      );
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const created = await seedDefaultWorkflows();
      if (created.length === 0) {
        addToast("info", "Default workflows already exist");
      } else {
        addToast("success", `Seeded ${created.length} default workflow(s)`);
      }
      await refresh();
    } catch (e: any) {
      addToast(
        "error",
        `Couldn't seed: ${e?.response?.data?.detail ?? "Try again."}`,
      );
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Approval Workflows</CardTitle>
            <p className="text-xs text-text-muted mt-1">
              Route leave requests, expenses, and other changes through the
              right approvers — with optional auto-approval gates.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={handleSeedDefaults}>
              <Wand2 className="h-4 w-4" />
              Seed Defaults
            </Button>
            <Button size="sm" onClick={() => setShowCreate(true)}>
              <Plus className="h-4 w-4" />
              New Workflow
            </Button>
          </div>
        </CardHeader>

        {loading ? (
          <p className="text-sm text-text-muted py-6 text-center">Loading…</p>
        ) : workflows.length === 0 ? (
          <p className="text-sm text-text-muted py-8 text-center">
            No workflows yet. Click <strong>Seed Defaults</strong> for a
            reasonable starting point.
          </p>
        ) : (
          <div className="space-y-6">
            {ENTITY_OPTIONS.map(({ value, label }) => {
              const list = grouped.get(value) ?? [];
              if (list.length === 0) return null;
              return (
                <div key={value}>
                  <h3 className="text-sm font-semibold text-text-primary mb-2">
                    {label}
                  </h3>
                  <div className="space-y-3">
                    {list.map((wf) => (
                      <WorkflowCard
                        key={wf.id}
                        workflow={wf}
                        onEdit={() => setEditing(wf)}
                        onDelete={() => handleDelete(wf)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {(showCreate || editing) && (
        <WorkflowModal
          initial={editing}
          onClose={() => {
            setShowCreate(false);
            setEditing(null);
          }}
          onSaved={async () => {
            setShowCreate(false);
            setEditing(null);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

function WorkflowCard({
  workflow,
  onEdit,
  onDelete,
}: {
  workflow: Workflow;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const steps = [...workflow.steps].sort((a, b) => a.step_order - b.step_order);
  return (
    <div className="rounded-lg border border-border/50 bg-surface p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-text-primary">{workflow.name}</p>
            <Badge variant={workflow.is_active ? "success" : "default"}>
              {workflow.is_active ? "Active" : "Inactive"}
            </Badge>
          </div>
          <p className="text-xs text-text-muted mt-0.5">
            {steps.length} step{steps.length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex gap-1">
          <button
            onClick={onEdit}
            className="p-1.5 rounded hover:bg-surface-hover text-text-muted hover:text-text-primary"
            title="Edit"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 rounded hover:bg-surface-hover text-text-muted hover:text-danger"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {steps.map((step, idx) => (
          <div key={step.id ?? idx}>
            <div className="flex items-center gap-3 rounded-md border border-border/40 bg-bg px-3 py-2">
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-accent/10 text-accent text-xs font-semibold">
                {step.step_order}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-text-primary truncate">
                  {summarizeApprover(step)}
                </p>
                <p className="text-xs text-text-muted truncate">
                  When: {summarizeCondition(step.condition)}
                </p>
              </div>
              {step.auto_approve && (
                <Badge variant="info">Auto-approve</Badge>
              )}
            </div>
            {idx < steps.length - 1 && (
              <div className="flex justify-center py-1">
                <ArrowDown className="h-3.5 w-3.5 text-text-muted" />
              </div>
            )}
          </div>
        ))}
        {steps.length === 0 && (
          <p className="text-xs text-text-muted italic">
            No steps — this workflow will not route anywhere.
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal (create + edit share one form)
// ---------------------------------------------------------------------------

function WorkflowModal({
  initial,
  onClose,
  onSaved,
}: {
  initial: Workflow | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const { addToast } = useToast();
  const [name, setName] = useState(initial?.name ?? "");
  const [entityType, setEntityType] = useState<EntityType>(
    initial?.entity_type ?? "leave_request",
  );
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  const [steps, setSteps] = useState<WorkflowStep[]>(
    initial
      ? [...initial.steps].sort((a, b) => a.step_order - b.step_order)
      : [emptyStep(1)],
  );
  const [saving, setSaving] = useState(false);

  const isEdit = Boolean(initial);

  const renumber = (list: WorkflowStep[]): WorkflowStep[] =>
    list.map((s, i) => ({ ...s, step_order: i + 1 }));

  const addStep = () =>
    setSteps((prev) => renumber([...prev, emptyStep(prev.length + 1)]));

  const removeStep = (idx: number) =>
    setSteps((prev) => renumber(prev.filter((_, i) => i !== idx)));

  const moveStep = (idx: number, dir: -1 | 1) => {
    setSteps((prev) => {
      const next = [...prev];
      const swap = idx + dir;
      if (swap < 0 || swap >= next.length) return prev;
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return renumber(next);
    });
  };

  const patchStep = (idx: number, patch: Partial<WorkflowStep>) => {
    setSteps((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    );
  };

  const handleSave = async () => {
    if (!name.trim()) {
      addToast("error", "Name is required");
      return;
    }
    // Validate approver refs.
    for (const s of steps) {
      if (s.approver_type === "specific_user" && !s.approver_id) {
        addToast("error", `Step ${s.step_order}: specific user not set`);
        return;
      }
      if (s.approver_type === "role" && !s.approver_role) {
        addToast("error", `Step ${s.step_order}: role not set`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        entity_type: entityType,
        is_active: isActive,
        steps,
      };
      if (isEdit && initial) {
        await updateWorkflow(initial.id, payload);
        addToast("success", "Workflow updated");
      } else {
        await createWorkflow(payload);
        addToast("success", "Workflow created");
      }
      await onSaved();
    } catch (e: any) {
      addToast(
        "error",
        `Couldn't save: ${e?.response?.data?.detail ?? "Try again."}`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={isEdit ? "Edit Workflow" : "New Workflow"}
      size="lg"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input
            label="Workflow Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Leave Request Approval"
            autoFocus
          />
          <Select
            label="Entity Type"
            value={entityType}
            onChange={(e) => setEntityType(e.target.value as EntityType)}
            options={ENTITY_OPTIONS}
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-text-primary">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="rounded border-border"
          />
          Active
        </label>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-text-primary">Steps</h4>
            <Button variant="secondary" size="sm" onClick={addStep}>
              <Plus className="h-3.5 w-3.5" />
              Add Step
            </Button>
          </div>
          <div className="space-y-3">
            {steps.map((step, idx) => (
              <StepEditor
                key={idx}
                index={idx}
                step={step}
                canMoveUp={idx > 0}
                canMoveDown={idx < steps.length - 1}
                onChange={(patch) => patchStep(idx, patch)}
                onMove={(d) => moveStep(idx, d)}
                onRemove={() => removeStep(idx)}
              />
            ))}
            {steps.length === 0 && (
              <p className="text-xs text-text-muted italic">
                Add at least one step.
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Workflow"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Step editor
// ---------------------------------------------------------------------------

function StepEditor({
  index,
  step,
  canMoveUp,
  canMoveDown,
  onChange,
  onMove,
  onRemove,
}: {
  index: number;
  step: WorkflowStep;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onChange: (patch: Partial<WorkflowStep>) => void;
  onMove: (d: -1 | 1) => void;
  onRemove: () => void;
}) {
  const hasCondition = !!step.condition;

  const setCondition = (patch: Partial<StepCondition>) => {
    const next: StepCondition = {
      field: step.condition?.field ?? "",
      operator: step.condition?.operator ?? "==",
      value: step.condition?.value ?? "",
      ...patch,
    };
    onChange({ condition: next });
  };

  return (
    <div className="rounded-lg border border-border/60 bg-bg p-3 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-text-muted uppercase tracking-wide">
          Step {index + 1}
        </p>
        <div className="flex gap-1">
          <button
            onClick={() => onMove(-1)}
            disabled={!canMoveUp}
            className="p-1 text-text-muted hover:text-text-primary disabled:opacity-30"
            title="Move up"
          >
            ↑
          </button>
          <button
            onClick={() => onMove(1)}
            disabled={!canMoveDown}
            className="p-1 text-text-muted hover:text-text-primary disabled:opacity-30"
            title="Move down"
          >
            ↓
          </button>
          <button
            onClick={onRemove}
            className="p-1 text-text-muted hover:text-danger"
            title="Remove step"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Select
          label="Approver"
          value={step.approver_type}
          onChange={(e) =>
            onChange({
              approver_type: e.target.value as ApproverType,
              approver_role: null,
              approver_id: null,
            })
          }
          options={APPROVER_OPTIONS}
        />
        {step.approver_type === "role" && (
          <Select
            label="Role"
            value={step.approver_role ?? ""}
            onChange={(e) => onChange({ approver_role: e.target.value })}
            options={ROLE_SUGGESTIONS}
            placeholder="Select a role"
          />
        )}
        {step.approver_type === "specific_user" && (
          <Input
            label="User ID"
            value={step.approver_id ?? ""}
            onChange={(e) =>
              onChange({ approver_id: e.target.value.trim() || null })
            }
            placeholder="UUID"
          />
        )}
      </div>

      <div className="rounded-md border border-border/40 p-3 space-y-2">
        <label className="flex items-center gap-2 text-sm text-text-primary">
          <input
            type="checkbox"
            checked={hasCondition}
            onChange={(e) =>
              onChange({
                condition: e.target.checked
                  ? { field: "", operator: "==", value: "" }
                  : null,
              })
            }
            className="rounded border-border"
          />
          Gate this step with a condition
        </label>
        {hasCondition && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <Input
              label="Field"
              value={step.condition!.field}
              onChange={(e) => setCondition({ field: e.target.value })}
              placeholder="e.g., total_days"
            />
            <Select
              label="Operator"
              value={step.condition!.operator}
              onChange={(e) =>
                setCondition({ operator: e.target.value as ConditionOperator })
              }
              options={OPERATOR_OPTIONS}
            />
            <Input
              label="Value"
              value={String(step.condition!.value ?? "")}
              onChange={(e) => {
                const raw = e.target.value;
                const asNum = Number(raw);
                const coerced =
                  raw !== "" && !Number.isNaN(asNum) ? asNum : raw;
                setCondition({ value: coerced });
              }}
              placeholder="e.g., 2"
            />
          </div>
        )}
      </div>

      <label className="flex items-center gap-2 text-sm text-text-primary">
        <input
          type="checkbox"
          checked={step.auto_approve}
          onChange={(e) => onChange({ auto_approve: e.target.checked })}
          className="rounded border-border"
        />
        Auto-approve (skips human sign-off when the condition is NOT met)
      </label>
    </div>
  );
}
